/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import cloneDeep from 'lodash/cloneDeep';
import { carExplorerOptions } from '../options/car-explorer.options';
import { browserHelper } from 'amb/helpers/browser.helper';
import initDragToClose, { observePopups } from 'honda_cars_ecux/utils/popupDragClose';
import { pannellumDirective } from './pannellum.directive';
const { carExplorerTracking } = require('../components/analytics');

/**
 * Car Explorer Directive
 *
 * Handles 360 spins, panoramas, and static image galleries with day/night mode.
 * All image data (including SFCC fallbacks) is pre-processed by the SirvAssetSync job
 * and stored in carExplorerData for a unified rendering experience.
 *
 * @example
 * ```html
 * <div amb-car-explorer data-car-explorer='{"version":"1.0",...}'>
 *   <!-- Content rendered by directive -->
 * </div>
 * ```
 *
 * @typedef {Object} AvailabilityState
 * @property {Record<string, Record<string, Record<string, boolean|null>>>} views
 * View availability by view > mode > timeOfDay. null = not checked, true/false = result
 *
 * @typedef {Object} State
 * @property {HTMLElement} element The DOM element
 * @property {string} baseUrl The base URL
 * @property {HTMLFormElement} form The form element
 * @property {Object} options The options
 * @property {Object} data The data
 * @property {string} currentTab The current tab
 * @property {string} currentTimeOfDay The current time of day
 * @property {boolean} is360Mode The 360 mode
 * @property {AvailabilityState} availability The availability state
 * @property {string} variantKey The current variant cache key
 * @property {string} [boundVariantKey] Variant whose 360 panes were last bound
 * @property {string} [gradeKey] Grade the explorer last showed
 * @property {boolean} [prefer360Pending] Switch to 360 once the current view's check resolves
 * @property {boolean} [userChoseStatic] Shopper turned 360 off for the current grade
 */
export const carExplorerDirective = {
  /**
   * Pre-defined options container
   */
  options: carExplorerOptions,

  /**
   * 360 slots the gallery can bind. Boot has no 360.
   *
   * @private
   * @readonly
   *
   * @type {Array<{ view: string, mode: string }>}
   */
  VIEW_360_SLOTS: [
    { view: 'exterior', mode: 'spin' },
    { view: 'interior', mode: 'panorama' }
  ],

  /**
   * Container for storing per-element state
   * Uses element.ambid as key for safe cleanup
   *
   * @type {Map<string, State>}
   */
  container: new Map(),

  /**
   * Cache for per-variant availability to avoid re-fetching
   * Key: variantKey (masterProductId/grade/color)
   * Value: AvailabilityState
   *
   * @type {Map<string, AvailabilityState>}
   */
  availabilityCache: new Map(),

  /**
   * Whether a variant's panorama hotspots file existed.
   * Key: `${variantKey}/${timeOfDay}`
   *
   * @private
   *
   * @type {Map<string, boolean>}
   */
  panoramaHotspotsCache: new Map(),

  /**
   * Sirv static folder or single image existed for a variant.
   * Key: `${variantKey}/${view}/${timeOfDay}`
   * Distinct from SFCC day availability, so a return visit only refetches a real Sirv hit.
   *
   * @private
   *
   * @type {Map<string, boolean>}
   */
  sirvStaticCache: new Map(),

  /**
   * Latest open static-gallery load per variant.
   * While a token is set, per-image results must not refresh controls.
   *
   * @private
   *
   * @type {Map<string, number>}
   */
  openStaticGallery: new Map(),

  /**
   * @private
   *
   * @type {number}
   */
  staticGalleryToken: 0,

  /**
   * Initialize directive on element
   *
   * @param {HTMLElement} element The DOM element
   * @param {object} options Resolved options from all sources
   */
  init(element, options = {}) {
    const presets = element.getAttribute('amb-car-explorer');
    const baseUrl = document.querySelector('meta[name="sirv-base-url"]')?.content?.trim() || '';

    // Create state object
    const state = {
      element,
      baseUrl,
      form: element.querySelector('form'),
      options,
      presets,
      isFullscreen: false,
      elements: this.cacheElements(element),
      handlers: {}
    };

    this.container.set(element.ambid, state);

    this.bindEvents(state);
    this.updateCarExplorer(state, options.product);
    this.bindDisclaimerModal(state);

    // Move the disclaimer modal to the body to avoid z-index issues
    if (state.elements.disclaimerModal) {
      document.body.append(state.elements.disclaimerModal);
    }
  },

  /**
   * Clean up when element is removed from DOM
   *
   * @param {HTMLElement} element The DOM element being removed
   */
  destroy(element) {
    const state = this.container.get(element.ambid);

    if (state) {
      this.unbindEvents(state);
      this.container.delete(element.ambid);
    }
  },

  /**
   * Cache DOM elements for quick access
   *
   * @param {HTMLElement} element Root element
   * @returns {object} Cached elements
   */
  cacheElements(element) {
    const form = element.querySelector('form');

    return {
      form,
      dropdown: element.querySelector('.car-explorer--dropdown'),
      disclaimerModal: element.querySelector('.car-explorer--disclaimer .modal'),
      ghost: element.nextElementSibling?.classList.contains('car-explorer--ghost') ?
        element.nextElementSibling :
        element.parentElement?.querySelector('.car-explorer--ghost'),
      // View controls
      views: {
        exterior: {
          input: form.querySelector('input[name="view"][value="exterior"]'),
          label: form.querySelector('label[for*="view-exterior"]'),
          dropdownLabel: form.querySelector('.car-explorer--dropdown label[for*="view-exterior"]')
        },
        interior: {
          input: form.querySelector('input[name="view"][value="interior"]'),
          label: form.querySelector('label[for*="view-interior"]'),
          dropdownLabel: form.querySelector('.car-explorer--dropdown label[for*="view-interior"]')
        },
        boot: {
          input: form.querySelector('input[name="view"][value="boot"]'),
          label: form.querySelector('label[for*="view-boot"]'),
          dropdownLabel: form.querySelector('.car-explorer--dropdown label[for*="view-boot"]')
        }
      },
      // Mode controls
      modes: {
        360: {
          input: form.querySelector('input[name="mode"][value="360"]'),
          label: form.querySelector('label[for*="mode-360"]')
        },
        night: {
          input: form.querySelector('input[name="mode"][value="night"]'),
          label: form.querySelector('label[for*="mode-night"]')
        },
        fullscreen: {
          input: form.querySelector('input[name="mode"][value="fullscreen"]'),
          label: form.querySelector('label[for*="mode-fullscreen"]')
        }
      }
    };
  },

  /**
   * Bind event handlers
   *
   * @param {State} state Element state
   */
  bindEvents(state) {
    const { element, elements: { form } } = state;

    state.handlers.onAttributeSelect = (event, data) => this.onAttributeSelect(state, event, data);
    state.handlers.onNavArrowClick = () => carExplorerTracking.trackNavArrow(state);

    $('body').on('product:afterAttributeSelect', state.handlers.onAttributeSelect);
    $(element).on('transitionend', event => this.onTransitionEnd(state, event));
    $(form).on('change', event => this.onFormChange(state, event));
    $(element).on('click', '.swiper-button-prev, .swiper-button-next', state.handlers.onNavArrowClick);

    this.observeHotspotPopups(state);
  },

  /**
   * Read the server-injected analytics context.
   * Mirrors the same source used by analytics.js helpers.context.
   * Provides sessionId and product.masterID / product.id reliably
   * across all page types (configurator, Page Designer, pdStorePage, etc.)
   *
   * @returns {{ sessionId: string|null, product: { masterID: string|null, id: string|null } }}
   */
  getAnalyticsContext() {
    if (this._analyticsContext === undefined) {
      const el = document.getElementById('analytics-context');
      this._analyticsContext = el ? JSON.parse(el.textContent) : {};
    }
    return this._analyticsContext;
  },

  /**
   * Unbind event handlers
   *
   * @param {object} state Element state
   */
  unbindEvents(state) {
    const { element, form, handlers: { onAttributeSelect, onNavArrowClick } } = state;

    $('body').off('product:afterAttributeSelect', onAttributeSelect);
    $(element).off('transitionend');
    $(form).off('change');
    $(element).off('click', '.swiper-button-prev, .swiper-button-next', onNavArrowClick);

    if (state._destroyPopupObserver) {
      state._destroyPopupObserver();
      state._destroyPopupObserver = null;
    }
  },

  /**
   * Observe Sirv spin hotspot popups (exterior 360) and attach drag-to-close.
   * Pannellum popups (interior) have data-drag-close and are skipped by the selector.
   */
  observeHotspotPopups(state) {
    state._destroyPopupObserver = observePopups({
      selector: '.sirv-hotspot-box-wrapper:not([data-drag-close])',
      overlaySelector: '.sirv-hotspot-box',
      scrollableSelector: '.sirv-hotspot-box-content'
    });
  },

  /**
   * @param {State} state
   */
  bindDisclaimerModal(state) {
    const { elements: { disclaimerModal } } = state;

    let destroyDisclaimerDrag;

    $('body')
      .on('show.bs.modal', '.disclaimer-modal', () => {
        destroyDisclaimerDrag = initDragToClose(disclaimerModal.querySelector('.disclaimer-modal .modal-dialog'), {
          scrollableSelector: '.disclaimer-modal .modal-body',
          onClose: () => $(disclaimerModal).modal('hide')
        });
      })
      .on('hide.bs.modal', '.disclaimer-modal', () => {
        if (destroyDisclaimerDrag) {
          destroyDisclaimerDrag();
          destroyDisclaimerDrag = null;
        }
      })
    ;
  },

  /**
   * @param {State} state
   * @param {TransitionEvent} event
   */
  onTransitionEnd(state, event) {
    const target = event.target;

    setImmediate(() => {
      if (target.classList.contains('car-explorer') && !target.classList.contains('fullscreen')) {
        // document.body.classList.remove('modal-open');
        browserHelper.showScrollbar();
      }

      if (target.role === 'tabpanel' && target.classList.contains('active') && !target.classList.contains('show')) {
        target.classList.remove('active');
      }
    });
  },

  /**
   *
   * @param {State} state
   * @param {JQuery.Event} event
   * @param {object} data
   */
  async onAttributeSelect(state, event, { container, data }) {
    this.updateCarExplorer(state, data.product);
  },

  /**
   *
   * @param {State} state
   * @param {Event} event
   */
  onFormChange(state, event) {
    const target = event.target;
    const { element, elements } = state;
    const { form } = elements;

    if (target.name === 'mode' && target.value === 'fullscreen') {
      if (target.checked) {
        this.openFullscreen(state);
      } else {
        this.closeFullscreen(state);
      }
    } else {
      if (target.name === 'view') {
        carExplorerTracking.trackZoneSwitch(state, target.value);
      }

      if (target.name === 'mode' && target.value === 'night') {
        carExplorerTracking.trackNightMode(state);
      }

      if (target.name === 'mode' && target.value === '360') {
        state.prefer360Pending = false;
        state.userChoseStatic = !target.checked;
      }

      const formData = new FormData(form);
      const requestedView = formData.get('view');
      const modes = formData.getAll('mode');
      const requestedIs360 = modes.includes('360');
      const requestedIsNight = modes.includes('night');

      // Determine the best mode/timeOfDay for the requested view based on availability
      const { mode: bestMode, timeOfDay: bestTimeOfDay } = this.getBestModeForView(
        state,
        requestedView,
        requestedIs360 ? '360' : 'static',
        requestedIsNight ? 'night' : 'day'
      );

      const is360Mode = bestMode === '360';
      const isNightMode = bestTimeOfDay === 'night';

      // Sync form controls with determined best mode
      if (elements.modes[360]?.input) {
        elements.modes[360].input.checked = is360Mode;
      }
      if (elements.modes.night?.input) {
        elements.modes.night.input.checked = isNightMode;
      }

      const selectedTabPane = this.getTabPane(state, requestedView, bestMode, bestTimeOfDay);
      const currentTabPane = element.querySelector('.car-explorer--content .tab-pane.active.show');

      element.setAttribute('data-view', requestedView);
      element.setAttribute('data-mode', bestMode);
      element.setAttribute('data-time-of-day', bestTimeOfDay);

      if (selectedTabPane && currentTabPane !== selectedTabPane) {
        const swiperEl = selectedTabPane.querySelector('[amb-swiper]');

        selectedTabPane.classList.add('active');

        if (swiperEl?.swiper && !swiperEl?.classList?.contains('swiper-initialized')) {
          swiperEl.swiper.init();
        }

        const dropdownToggle = form.querySelector('.car-explorer--dropdown .dropdown-toggle');
        const dropdownLabel = form.querySelector(`.car-explorer--dropdown label[for="${target.id}"]`);
        const currentDropdownLabel = form.querySelector('.car-explorer--dropdown label.active');

        if (currentDropdownLabel) {
          currentDropdownLabel.classList.remove('active');
        }

        if (dropdownToggle && dropdownLabel) {
          dropdownLabel.classList.add('active');
          dropdownToggle.textContent = dropdownLabel.textContent;
        }

        setImmediate(() => {
          selectedTabPane.classList.add('show');

          if (currentTabPane) {
            currentTabPane.classList.remove('show');
          }
        });
      }

      // Update controls visibility after view/mode change
      this.updateControlsVisibility(state);
    }
  },

  /**
   * Determine the best mode and timeOfDay for a view based on availability
   *
   * Priority logic:
   * 1. If requested mode is available for the view → use it
   * 2. If requested mode is NOT available:
   *    - If 360 was requested but unavailable → try static
   *    - If static was requested but unavailable/not configured → try 360
   * 3. For timeOfDay: if requested time unavailable → fallback to day
   *
   * @param {State} state Element state
   * @param {string} view The view to check
   * @param {string} requestedMode The requested mode ('360' or 'static')
   * @param {string} requestedTimeOfDay The requested time of day ('day' or 'night')
   * @returns {{mode: string, timeOfDay: string}} Best mode and timeOfDay
   */
  getBestModeForView(state, view, requestedMode, requestedTimeOfDay) {
    const has360 = this.is360Available(state, view);
    const hasStatic = this.isStaticAvailable(state, view);
    const isStaticConfigured = this.isStaticConfigured(state, view);

    let bestMode = requestedMode;
    let bestTimeOfDay = requestedTimeOfDay;

    if (requestedMode === '360') {
      if (has360) {
        // 360 requested and available
        bestMode = '360';
      } else if (hasStatic) {
        // 360 requested but unavailable, fallback to static
        bestMode = 'static';
      }
      // else: neither available, keep 360 (will show empty)
    } else {
      // Static requested
      if (isStaticConfigured && hasStatic) {
        // Static configured and available
        bestMode = 'static';
      } else if (has360) {
        // Static not configured or unavailable, but 360 is available
        bestMode = '360';
      } else if (hasStatic) {
        // Static exists (as fallback content) even if not configured
        bestMode = 'static';
      }
      // else: neither available, keep static (will show empty)
    }

    // Map mode to availability key for timeOfDay check
    const modeKey = bestMode === '360' ?
      (view === 'interior' ? 'panorama' : 'spin') :
      'static';

    // Check if requested timeOfDay is available
    if (state.availability?.[view]?.[modeKey]?.[requestedTimeOfDay] === true) {
      bestTimeOfDay = requestedTimeOfDay;
    } else if (bestMode === '360' && state.availability?.[view]?.static?.[requestedTimeOfDay] === true) {
      // 360 doesn't have requested timeOfDay, fall back to static which does
      bestMode = 'static';
      bestTimeOfDay = requestedTimeOfDay;
    } else if (state.availability?.[view]?.[modeKey]?.day === true) {
      // Fallback to day
      bestTimeOfDay = 'day';
    } else if (state.availability?.[view]?.[modeKey]?.night === true) {
      // Day not available, try night
      bestTimeOfDay = 'night';
    }

    return { mode: bestMode, timeOfDay: bestTimeOfDay };
  },

  async updateCarExplorer(state, product) {
    const { element, options } = state;
    const { productName = '', customProps = {}, images = {}, variationAttributes = [], inventoryModelYear } = product || {};
    const { interior: interiorImages = [], large: exteriorImages = [], boot: bootImages = [] } = images;
    const { FullYear: productYear } = customProps;
    // Prefer inventory model year (from assigned IVN) over the product-level FullYear
    const displayYear = inventoryModelYear || productYear;
    const grade = variationAttributes.find((attr) => attr.id === 'grade')?.values.find((v) => v.selected);

    // Generate variant cache key
    const variantKey = this.getVariantCacheKey(product);
    const variantChanged = state.boundVariantKey !== variantKey;
    const gradeKey = grade?.value || '';
    let reloadStaticGallery = false;

    state.variantKey = variantKey;
    state.product = product;

    const gradeChanged = state.gradeKey !== gradeKey;

    if (gradeChanged) {
      state.userChoseStatic = false;
    }

    if (options.defaultMode === '360' && variantChanged && !state.userChoseStatic) {
      state.prefer360Pending = true;
    }

    state.gradeKey = gradeKey;

    if (variantChanged) {
      this.clearConfigured360Panes(state);
      this.clearNightStaticPanes(state);
      state.boundVariantKey = variantKey;
    }

    if (!state.baseUrl) {
      state.availability = this.initAvailabilityState(state, options);
      this.setStaticImageAvailability(state, images);
      this.markSirvUnavailable(state);
      this.updateControlsVisibility(state);
    } else {
      const cachedAvailability = this.availabilityCache.get(variantKey);
      const cacheReady = cachedAvailability && this.isSirvCheckSettled(state, cachedAvailability);

      if (cacheReady) {
        state.availability = cloneDeep(cachedAvailability);

        if (variantChanged) {
          this.rebind360Viewers(state, variantKey);
          reloadStaticGallery = true;

          if (state.prefer360Pending) {
            this.updateControlsVisibility(state);
          }
        } else {
          this.updateControlsVisibility(state);
        }
      } else if (variantChanged) {
        state.availability = this.initAvailabilityState(state, options);
        this.setStaticImageAvailability(state, images);

        this.checkSirvAvailability(state, product, variantKey)
          .then(() => {
            if (state.variantKey !== variantKey) {
              return;
            }

            this.updateControlsVisibility(state);
          })
        ;
      }
    }

    // Update product info display
    const content = {
      '.car-explorer--name': productName.replace(/^(\s*(\d{4}|Build)\s*)+/g, ''),
      '.car-explorer--grade': grade?.displayValue,
      '.car-explorer--year': displayYear
    };

    for (const [selector, value] of Object.entries(content)) {
      const el = element.querySelector(selector);

      if (el) {
        el.textContent = value;
        el.classList.toggle('d-none', !value);
      }
    }

    // Update static image swipers
    if (variantChanged) {
      this.updateStaticSwipers(state, {
        exterior: exteriorImages,
        interior: interiorImages,
        boot: bootImages
      });
    }

    if (reloadStaticGallery) {
      const galleryToken = this.beginStaticGallery(variantKey);

      this.loadConfiguredStaticGallery(state, variantKey)
        .then(() => {
          const stillLatest = this.endStaticGallery(variantKey, galleryToken);

          if (state.variantKey !== variantKey || !stillLatest) {
            return;
          }

          this.updateControlsVisibility(state);
        })
      ;
    }
  },

  /**
   * Update static image swipers for all views
   *
   * @param {State} state Element state
   * @param {Object} imagesByView Images grouped by view
   */
  updateStaticSwipers(state, imagesByView) {
    const { options } = state;

    for (const [view, modes] of Object.entries(options.views || {})) {
      for (const timeOfDay of modes.static || []) {
        if (timeOfDay !== 'day') {
          continue;
        }

        const tabPane = this.getTabPane(state, view, 'static', timeOfDay);
        const swiper = tabPane?.querySelector('.swiper')?.swiper;

        if (swiper && imagesByView[view]) {
          this.updateSwiperImages(swiper, imagesByView[view]);
        }
      }
    }
  },


  /**
   * Update the static swiper for a view with images
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior, interior, boot)
   * @param {string} timeOfDay Time of day (day, night)
   * @param {Array} images Array of image objects
   */
  updateStaticSwiper(state, view, timeOfDay, images) {
    const tabPane = this.getTabPane(state, view, 'static', timeOfDay);
    const swiper = tabPane?.querySelector('.swiper')?.swiper;

    if (swiper) {
      this.updateSwiperImages(swiper, images);
    }
  },

  /**
   *
   * @param {import('swiper').Swiper} swiper
   * @param {object[]} images
   */
  updateSwiperImages(swiper, images = []) {
    const writeId = (swiper._imageWriteId || 0) + 1;

    swiper._imageWriteId = writeId;

    // Prefetch the first image to avoid FOUC
    Promise
      .all(images.map((image, index) => new Promise((resolve, reject) => {
        if (index > 0) {
          resolve();
        } else {
          const img = new Image();

          img.src = image.url;
          img.onload = resolve;
          img.onerror = reject;
        }
      })))
      .finally(() => {
        if (swiper._imageWriteId !== writeId) {
          return;
        }

        // No loop when only one slide (no clone, no swipe)
        const noLoopSingleSlide = swiper.params?.noLoopSingleSlide !== false;
        // Preserve camera angle across variant changes (realIndex ignores loop clones)
        const currentRealIndex = swiper.realIndex ?? 0;

        if (noLoopSingleSlide && images.length <= 1 && swiper.params.loop) {
          swiper._initialLoop = true;
          swiper.params.loop = false;
        } else if (noLoopSingleSlide && images.length > 1 && swiper._initialLoop) {
          swiper.params.loop = true;
          swiper._initialLoop = false;
        }

        swiper.wrapperEl.innerHTML = '';
        swiper.appendSlide(images.map((image) => `<div class="swiper-slide"><img class="slide-image" src="${image.url}" alt="${image.alt || image.title || ''}" /></div>`));
        swiper.update();

        const targetIndex = Math.min(currentRealIndex, Math.max(images.length - 1, 0));
        if (swiper.params.loop && typeof swiper.slideToLoop === 'function') {
          swiper.slideToLoop(targetIndex, 0);
        } else {
          swiper.slideTo(targetIndex, 0);
        }
        swiper.emit('slidesLengthChange');
      })
    ;
  },

  /**
   * Fetch static images from a Sirv folder and update the static swiper
   * Supports both folder-based multiple images and single image fallback.
   * Also handles background images (background.* in folder, or {view}-background.jpg for single).
   *
   * Priority:
   * 1. Folder with multiple images: /{productPath}/{timeOfDay}/{view}/
   * 2. Single image fallback: /{productPath}/{timeOfDay}/{view}.jpg
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior, interior, boot)
   * @param {string} productPath Product path for Sirv URLs
   * @param {string} timeOfDay Time of day (day, night)
   * @returns {Promise<boolean>} Promise resolving to true if images found, false otherwise
   */
  fetchSirvStaticImages(state, view, productPath, timeOfDay) {
    const { baseUrl } = state;
    const folderPath = `${baseUrl}/${productPath}/${timeOfDay}/${view}`;
    const singleImagePath = `${baseUrl}/${productPath}/${timeOfDay}/${view}.jpg`;
    const backgroundPath = `${baseUrl}/${productPath}/${timeOfDay}/${view}-background.jpg`;

    // Try folder first, then fallback to single image
    return fetch(folderPath, { method: 'GET' })
      .then((res) => res.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const images = Array.from(doc.querySelectorAll('a[href]'))
          .map((a) => a.getAttribute('href'))
          .filter((href) => /\.(jpe?g|png|webp|gif)$/i.test(href))
          .map((href) => {
            const imageUrl = href.startsWith('//') ? `https:${href}` : href;
            return { alt: '', url: imageUrl, title: '', absURL: imageUrl };
          });

        if (images.length > 0) {
          this.rememberSirvStatic(productPath, view, timeOfDay, true);

          if (state.variantKey === productPath) {
            this.updateStaticSwiper(state, view, timeOfDay, images);
            this.fetchTabPaneBackground(state, view, 'static', timeOfDay, backgroundPath);
          }

          this.recordStaticAvailability(state, productPath, view, timeOfDay, true);

          return true;
        }

        return this.fetchSingleStaticImage(state, view, productPath, timeOfDay, singleImagePath, backgroundPath);
      })
      .catch(() => this.fetchSingleStaticImage(state, view, productPath, timeOfDay, singleImagePath, backgroundPath));
  },

  /**
   * Fetch a single static image and update the swiper
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior, interior, boot)
   * @param {string} productPath Variant the check belongs to
   * @param {string} timeOfDay Time of day (day, night)
   * @param {string} imageUrl URL of the single image
   * @param {string} [backgroundUrl] Optional URL of the background image
   *
   * @returns {Promise<boolean>} Promise resolving to true if image exists, false otherwise
   */
  fetchSingleStaticImage(state, view, productPath, timeOfDay, imageUrl, backgroundUrl) {
    const availability = state.variantKey === productPath ?
      state.availability :
      this.availabilityCache.get(productPath);
    const keepSfccDay = timeOfDay === 'day' && availability?.[view]?.static?.day === true;

    return fetch(imageUrl, { method: 'HEAD' })
      .then((response) => {
        const stillCurrent = state.variantKey === productPath;
        const isAvailable = response.ok;

        this.rememberSirvStatic(productPath, view, timeOfDay, isAvailable);

        if (isAvailable && stillCurrent) {
          const images = [{
            alt: '',
            url: response.url,
            title: '',
            absURL: response.url
          }];

          this.updateStaticSwiper(state, view, timeOfDay, images);

          if (backgroundUrl) {
            this.fetchTabPaneBackground(state, view, 'static', timeOfDay, backgroundUrl);
          }
        }

        const available = isAvailable || keepSfccDay;

        if (isAvailable || !keepSfccDay) {
          this.recordStaticAvailability(state, productPath, view, timeOfDay, available);
        }

        return available;
      })
      .catch(() => {
        this.rememberSirvStatic(productPath, view, timeOfDay, false);

        if (!keepSfccDay) {
          this.recordStaticAvailability(state, productPath, view, timeOfDay, false);
        }

        return keepSfccDay;
      });
  },

  /**
   * Set background image on a tab pane element
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior, interior, boot)
   * @param {string} mode Mode (static, 360)
   * @param {string} timeOfDay Time of day (day, night)
   * @param {string} backgroundUrl URL of the background image
   */
  setTabPaneBackground(state, view, mode, timeOfDay, backgroundUrl) {
    const tabPane = this.getTabPane(state, view, mode, timeOfDay);

    if (tabPane && backgroundUrl) {
      tabPane.style.backgroundImage = `url('${backgroundUrl}')`;
    }
  },

  /**
   * Fetch and set background image for a tab pane (single image check)
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior, interior, boot)
   * @param {string} mode Mode (static, 360)
   * @param {string} timeOfDay Time of day (day, night)
   * @param {string} backgroundUrl URL of the background image to check
   * @returns {Promise<boolean>} Promise resolving to true if background found
   */
  fetchTabPaneBackground(state, view, mode, timeOfDay, backgroundUrl) {
    const variantKey = state.variantKey;

    return fetch(backgroundUrl, { method: 'HEAD' })
      .then((response) => {
        if (state.variantKey !== variantKey) {
          return false;
        }

        if (response.ok) {
          this.setTabPaneBackground(state, view, mode, timeOfDay, response.url);
        }

        return response.ok;
      })
      .catch(() => false);
  },

  /**
   * @param {State} state
   * @param {string} view
   * @param {string} mode
   * @param {string} timeOfDay
   *
   * @returns {HTMLElement|null}
   */
  getTabPane(state, view = 'exterior', mode = 'static', timeOfDay = 'day') {
    const { element } = state;

    return element.querySelector(`.car-explorer--content .tab-pane[view="${view}"][mode="${mode}"][time-of-day="${timeOfDay}"]`);
  },

  // ============================================================================
  // Availability State Management
  // ============================================================================

  /**
   * Generate cache key from product variant
   *
   * @param {Object} product Product data
   * @returns {string} Cache key
   */
  getVariantCacheKey(product) {
    const { id, parentProductId: masterProductId, variationAttributes = [] } = product || {};
    const grade = variationAttributes.find((attr) => attr.id === 'grade')?.values.find((v) => v.selected);
    const color = variationAttributes.find((attr) => attr.id === 'color')?.values.find((v) => v.selected);

    return [masterProductId || id, grade?.value, color?.value].filter(Boolean).join('/');
  },

  /**
   * Initialize availability state from options configuration
   * Sets initial state: configured items are null (pending), non-configured are false (unavailable)
   *
   * @param {State} state Element state
   * @param {Object} options Component options
   * @returns {AvailabilityState} Initial availability state
   */
  initAvailabilityState(state, options) {
    const availability = {
      exterior: {
        static: { day: false, night: false },
        spin: { day: null, night: null }
      },
      interior: {
        static: { day: false, night: false },
        panorama: { day: null, night: null }
      },
      boot: {
        static: { day: false, night: false },
        spin: { day: null, night: null }
      }
    };

    // Mark configured modes as pending (null) or unavailable (false)
    for (const [view, modes] of Object.entries(options.views || {})) {
      for (const [mode, timesOfDay] of Object.entries(modes)) {
        for (const tod of ['day', 'night']) {
          if (availability[view]?.[mode]) {
            // If configured in options, mark as pending (null), otherwise unavailable (false)
            availability[view][mode][tod] = timesOfDay.includes(tod) ? null : false;
          }
        }
      }
    }

    return availability;
  },

  /**
   * Update availability for a specific view/mode/timeOfDay
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior, interior, boot)
   * @param {string} mode Mode name (static, spin, panorama)
   * @param {string} timeOfDay Time of day (day, night)
   * @param {boolean} isAvailable Whether the resource is available
   */
  setAvailability(state, view, mode, timeOfDay, isAvailable) {
    if (state.availability?.[view]?.[mode]) {
      state.availability[view][mode][timeOfDay] = isAvailable;

      // Update cache
      if (state.variantKey) {
        this.availabilityCache.set(state.variantKey, cloneDeep(state.availability));
      }
    }
  },

  /**
   * Check if a view has any available content
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @returns {boolean} True if view has any available content
   */
  isViewAvailable(state, view) {
    const viewAvailability = state.availability?.[view];

    if (!viewAvailability) return false;

    for (const modes of Object.values(viewAvailability)) {
      for (const isAvailable of Object.values(modes)) {
        if (isAvailable === true) return true;
      }
    }

    return false;
  },

  /**
   * Check if 360 mode (spin/panorama) is available for a view
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @param {string|null} [timeOfDay=null] Time of day to check (day/night). If null, checks if ANY time is available
   * @returns {boolean} True if 360 mode is available
   */
  is360Available(state, view, timeOfDay = null) {
    const mode360Key = view === 'interior' ? 'panorama' : 'spin';
    const modeAvailability = state.availability?.[view]?.[mode360Key];

    if (!modeAvailability) return false;

    if (timeOfDay) {
      return modeAvailability[timeOfDay] === true;
    }

    return modeAvailability.day === true || modeAvailability.night === true;
  },

  /**
   * Check if static mode is available for a view
   * Static is available if configured in options AND has content
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @param {string|null} [timeOfDay=null] Time of day to check (day/night). If null, checks if ANY time is available
   * @returns {boolean} True if static mode is available
   */
  isStaticAvailable(state, view, timeOfDay = null) {
    const staticAvailability = state.availability?.[view]?.static;

    if (!staticAvailability) return false;

    if (timeOfDay) {
      return staticAvailability[timeOfDay] === true;
    }

    return staticAvailability.day === true || staticAvailability.night === true;
  },

  /**
   * Check if static mode is configured (enabled) for a view in options
   * This differs from isStaticAvailable - this checks config, not content
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @returns {boolean} True if static mode is configured
   */
  isStaticConfigured(state, view) {
    const { options } = state;
    const staticConfig = options.views?.[view]?.static;

    return Array.isArray(staticConfig) && staticConfig.length > 0;
  },

  /**
   * Check if a disclaimer has been configured for a view
   * Maps view name to the corresponding product custom attribute (e.g. exterior → ExteriorDisclaimer)
   *
   * @param {State} state Element state
   * @param {string} view View name (exterior | interior | boot)
   * @returns {boolean} True if disclaimer content is configured for the view
   */
  isDisclaimerConfigured(state, view) {
    const { customProps = {} } = state.product || {};
    const attributeKey = `${view.charAt(0).toUpperCase() + view.slice(1)}Disclaimer`;

    return !!customProps[attributeKey];
  },

  /**
   * Update the disclaimer modal content for the current view and product
   *
   * Mirrors the ISML scaffold logic: auto-wraps BM HTML with .modal-dialog /
   * .modal-content / .modal-body only when the authored content does not
   * already include those elements. A close button is injected unless the
   * authored content supplies its own.
   *
   * @param {State} state Element state
   */
  updateDisclaimerModal(state) {
    const { element, elements } = state;
    const { disclaimerModal } = elements;

    if (!disclaimerModal) return;

    const currentView = element.getAttribute('data-view') || 'exterior';
    const { customProps = {} } = state.product || {};
    const disclaimerHtml = customProps[`${currentView.charAt(0).toUpperCase() + currentView.slice(1)}Disclaimer`] || '';

    if (!disclaimerHtml) {
      disclaimerModal.innerHTML = '';
      return;
    }

    const hasModalDialog = /class="(.*\s)?modal-dialog(\s.*)?"/i.test(disclaimerHtml);
    const hasModalContent = /class="(.*\s)?modal-content(\s.*)?"/i.test(disclaimerHtml);
    const hasModalHeader = /class="(.*\s)?modal-header(\s.*)?"/i.test(disclaimerHtml);
    const hasModalBody = /class="(.*\s)?modal-body(\s.*)?"/i.test(disclaimerHtml);
    const hasCloseBtn = /class="(.*\s)?close(\s.*)?"/i.test(disclaimerHtml);

    let innerHtml = disclaimerHtml;

    if (!hasModalBody) {
      innerHtml = `<div class="modal-body">${innerHtml}</div>`;
    }

    if (!hasModalContent) {
      const headerHtml = !hasModalHeader ? '<div class="modal-header"></div>' : '';
      innerHtml = `<div class="modal-content">${headerHtml}${innerHtml}</div>`;
    }

    if (!hasModalDialog) {
      const closeBtnHtml = !hasCloseBtn ?
        '<button type="button" class="close" data-dismiss="modal" aria-label="Close"><span class="label">Close</span></button>' :
        ''
      ;

      innerHtml = `<div class="modal-dialog">${closeBtnHtml}${innerHtml}</div>`;
    }

    disclaimerModal.innerHTML = innerHtml;
  },

  /**
   * Check if night mode is available for current view and mode
   *
   * @param {State} state Element state
   * @returns {boolean} True if night mode is available
   */
  isNightAvailable(state) {
    const { element } = state;
    const currentView = element.getAttribute('data-view') || 'exterior';
    const currentMode = element.getAttribute('data-mode') || 'static';

    // Map UI mode to availability mode key
    const modeKey = currentMode === '360' ?
      (currentView === 'interior' ? 'panorama' : 'spin') :
      'static';

    return state.availability?.[currentView]?.[modeKey]?.night === true ||
      (currentMode === '360' && state.availability?.[currentView]?.static?.night === true);
  },

  /**
   * Update UI controls visibility based on current availability state
   *
   * @param {State} state Element state
   */
  updateControlsVisibility(state) {
    const { element, elements } = state;
    const preferredMode = this.takePreferredMode(state);

    if (preferredMode) {
      this.handleFallback(state, preferredMode);
    }

    const currentView = element.getAttribute('data-view') || 'exterior';
    const currentTimeOfDay = element.getAttribute('data-time-of-day') || 'day';

    // Update view tab visibility
    for (const view of ['exterior', 'interior', 'boot']) {
      const viewElements = elements.views[view];
      const isAvailable = this.isViewAvailable(state, view);

      viewElements?.label?.classList.toggle('d-none', !isAvailable);
      viewElements?.dropdownLabel?.classList.toggle('d-none', !isAvailable);
    }

    // Update 360 mode button state (check availability for current time of day)
    const has360 = this.is360Available(state, currentView, currentTimeOfDay);
    const hasStatic = this.isStaticAvailable(state, currentView, currentTimeOfDay);
    const isStaticConfigured = this.isStaticConfigured(state, currentView);
    const mode360Elements = elements.modes[360];

    if (mode360Elements?.label) {
      // Hidden: Exterior night (static only) or no 360 available
      // Visible + readonly: 360 available but no static (can't toggle off)
      // Visible + clickable: Both 360 and static available
      const hide360 = !has360;
      mode360Elements.label.classList.toggle('d-none', hide360);
      if (mode360Elements.input) {
        mode360Elements.input.classList.toggle('d-none', hide360);
      }

      // Add readonly state when 360 is available but static is not configured/available (and not hidden)
      const isReadonly = !hide360 && has360 && (!isStaticConfigured || !hasStatic);

      mode360Elements.label.classList.toggle('readonly', isReadonly);
      if (mode360Elements.input) {
        mode360Elements.input.disabled = isReadonly;
        // When static is not configured, 360 should be checked by default
        if (has360 && !isStaticConfigured) {
          mode360Elements.input.checked = true;
        }
      }
    }

    // Update night mode button visibility
    const hasNight = this.isNightAvailable(state);
    elements.modes.night?.label?.classList.toggle('d-none', !hasNight);
    element.setAttribute('data-has-360', has360);
    element.setAttribute('data-has-night', hasNight);
    element.setAttribute('data-has-disclaimer', this.isDisclaimerConfigured(state, currentView));

    this.updateDisclaimerModal(state);

    // Handle fallback if current selection is unavailable
    this.handleFallback(state);
  },

  /**
   * Handle fallback when current view/mode/timeOfDay is unavailable
   * Uses getBestModeForView to determine the correct state
   *
   * @param {State} state Element state
   * @param {string} [requestedMode] Mode to try instead of the current one
   */
  handleFallback(state, requestedMode) {
    const { element, elements } = state;
    const currentView = element.getAttribute('data-view') || 'exterior';
    const currentMode = element.getAttribute('data-mode') || 'static';
    const currentTimeOfDay = element.getAttribute('data-time-of-day') || 'day';

    let newView = currentView;

    // Check if current view is available
    if (!this.isViewAvailable(state, currentView)) {
      // Find first available view
      for (const view of ['exterior', 'interior', 'boot']) {
        if (this.isViewAvailable(state, view)) {
          newView = view;
          break;
        }
      }
    }

    // Determine the best mode and timeOfDay for the view
    const { mode: newMode, timeOfDay: newTimeOfDay } = this.getBestModeForView(
      state,
      newView,
      requestedMode || currentMode,
      currentTimeOfDay
    );

    // Sync form controls
    if (elements.modes[360]?.input) {
      elements.modes[360].input.checked = newMode === '360';
    }
    if (elements.modes.night?.input) {
      elements.modes.night.input.checked = newTimeOfDay === 'night';
    }

    // Apply changes if needed
    if (newView !== currentView || newMode !== currentMode || newTimeOfDay !== currentTimeOfDay) {
      element.setAttribute('data-view', newView);
      element.setAttribute('data-mode', newMode);
      element.setAttribute('data-time-of-day', newTimeOfDay);

      // Update view radio and mobile dropdown active state if changed
      if (newView !== currentView && elements.views[newView]?.input) {
        const viewInput = elements.views[newView].input;
        viewInput.checked = true;

        const dropdownToggle = elements.form.querySelector('.car-explorer--dropdown .dropdown-toggle');
        const dropdownLabel = elements.form.querySelector(`.car-explorer--dropdown label[for="${viewInput.id}"]`);
        const currentDropdownLabel = elements.form.querySelector('.car-explorer--dropdown label.active');

        if (currentDropdownLabel) {
          currentDropdownLabel.classList.remove('active');
        }

        if (dropdownToggle && dropdownLabel) {
          dropdownLabel.classList.add('active');
          dropdownToggle.textContent = dropdownLabel.textContent;
        }
      }

      // Update active tab pane
      const selectedTabPane = this.getTabPane(state, newView, newMode, newTimeOfDay);
      const currentTabPane = element.querySelector('.car-explorer--content .tab-pane.active.show');

      if (selectedTabPane && selectedTabPane !== currentTabPane) {
        currentTabPane?.classList.remove('active', 'show');
        selectedTabPane.classList.add('active', 'show');
      }
    }
  },

  /**
   * Check Sirv availability for all configured modes
   * Updates availability state as each check completes
   *
   * @param {State} state Element state
   * @param {Object} product Product data
   * @param {string} productPath Product path for Sirv URLs
   */
  async checkSirvAvailability(state, product, productPath) {
    const { baseUrl, options } = state;
    const { customProps = {} } = product || {};
    const { SpinAvailable: spinAvailable, PanoramaAvailable: panoramaAvailable } = customProps;

    if (!baseUrl) {
      this.markSirvUnavailable(state);

      return;
    }

    const checks = [];

    // Check exterior spin availability
    if (spinAvailable && options.views?.exterior?.spin?.length > 0) {
      for (const timeOfDay of options.views.exterior.spin) {
        const url = `${baseUrl}/${productPath}/${timeOfDay}/360/360.spin`;

        checks.push(
          fetch(url, { method: 'HEAD' })
            .then((response) => {
              const stillCurrent = this.applyAvailabilityResult(state, productPath, 'exterior', 'spin', timeOfDay, response.ok);

              if (response.ok) {
                if (stillCurrent) {
                  this.bindExteriorSpin(state, productPath, timeOfDay);

                  const staticFetch = this.fetchConfiguredStatic(state, 'exterior', productPath, timeOfDay);

                  if (staticFetch) {
                    return staticFetch;
                  }
                }
              } else if (response.status === 404) {
                return this.fetchStaticOnMiss(state, 'exterior', productPath, timeOfDay);
              }

              return response.ok;
            })
            .catch(() => {
              this.applyAvailabilityResult(state, productPath, 'exterior', 'spin', timeOfDay, false);

              return false;
            })
        );
      }
    } else {
      // 360 not configured — mark spin unavailable and check Sirv static directly
      this.setAvailability(state, 'exterior', 'spin', 'day', false);
      this.setAvailability(state, 'exterior', 'spin', 'night', false);

      for (const timeOfDay of options.views?.exterior?.static || []) {
        checks.push(this.fetchSirvStaticImages(state, 'exterior', productPath, timeOfDay));
      }
    }

    // Check interior panorama availability
    if (panoramaAvailable && options.views?.interior?.panorama?.length > 0) {
      for (const timeOfDay of options.views.interior.panorama) {
        const panoramaUrl = `${baseUrl}/${productPath}/${timeOfDay}/panorama.jpg`;
        const hotspotsUrl = `${baseUrl}/${productPath}/${timeOfDay}/panorama.json`;

        checks.push(
          Promise.all([
            fetch(panoramaUrl, { method: 'HEAD' }).catch(() => null),
            fetch(hotspotsUrl, { method: 'HEAD' }).catch(() => null)
          ])
            .then(([panoramaResponse, hotspotsResponse]) => {
              if (!panoramaResponse) {
                this.applyAvailabilityResult(state, productPath, 'interior', 'panorama', timeOfDay, false);

                return false;
              }

              const isAvailable = panoramaResponse.ok;
              const hasHotspots = Boolean(hotspotsResponse && hotspotsResponse.ok);

              this.panoramaHotspotsCache.set(`${productPath}/${timeOfDay}`, hasHotspots);

              const stillCurrent = this.applyAvailabilityResult(state, productPath, 'interior', 'panorama', timeOfDay, isAvailable);

              if (isAvailable) {
                if (stillCurrent) {
                  this.bindInteriorPanorama(state, productPath, timeOfDay, hasHotspots);

                  const staticFetch = this.fetchConfiguredStatic(state, 'interior', productPath, timeOfDay);

                  if (staticFetch) {
                    return staticFetch;
                  }
                }
              } else if (panoramaResponse.status === 404) {
                return this.fetchStaticOnMiss(state, 'interior', productPath, timeOfDay);
              }

              return isAvailable;
            })
        );
      }
    } else {
      // 360 not configured — mark panorama unavailable and check Sirv static directly
      this.setAvailability(state, 'interior', 'panorama', 'day', false);
      this.setAvailability(state, 'interior', 'panorama', 'night', false);

      for (const timeOfDay of options.views?.interior?.static || []) {
        checks.push(this.fetchSirvStaticImages(state, 'interior', productPath, timeOfDay));
      }
    }

    // Check boot static images from Sirv (boot is static-only, no 360)
    for (const timeOfDay of options.views?.boot?.static || []) {
      checks.push(this.fetchSirvStaticImages(state, 'boot', productPath, timeOfDay));
    }

    // Wait for all checks to complete
    await Promise.allSettled(checks);
  },

  /**
   * Set static image availability from SFCC images
   *
   * @param {State} state Element state
   * @param {Object} images Product images
   */
  setStaticImageAvailability(state, images) {
    const { large: exteriorImages = [], interior: interiorImages = [], boot: bootImages = [] } = images;

    // Exterior static images (from SFCC)
    this.setAvailability(state, 'exterior', 'static', 'day', exteriorImages.length > 0);

    // Interior static images (from SFCC)
    this.setAvailability(state, 'interior', 'static', 'day', interiorImages.length > 0);

    // Boot static images (from SFCC)
    this.setAvailability(state, 'boot', 'static', 'day', bootImages.length > 0);

    // Night static images are not available from SFCC
    this.setAvailability(state, 'exterior', 'static', 'night', false);
    this.setAvailability(state, 'interior', 'static', 'night', false);
    this.setAvailability(state, 'boot', 'static', 'night', false);
  },

  /**
   * Open fullscreen mode using FLIP animation
   *
   * @param {State} state Element state
   */
  openFullscreen(state) {
    const { element, elements } = state;
    const { ghost } = elements;

    if (state.isFullscreen || !ghost) return;

    // GA tracking for fullscreen click
    try {
      const analyticsCtx = this.getAnalyticsContext();
      const currentView = element.getAttribute('data-view') || 'exterior';
      const modelGrade = element.querySelector('.car-explorer--grade')?.textContent?.trim() || null;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        // GA4-safe, descriptive event name
        event: 'full_screen',

        // Core descriptors
        action: 'click',
        content_type: 'configurator',
        context: currentView, // dynamically pass zone value (exterior, interior, boot)
        location: 'car_explorer',
        ui_experience: 'configurator',

        // Business / model metadata
        model_grade: modelGrade,
        model_id: analyticsCtx.product?.masterID
          || analyticsCtx.product?.id
          || null,

        // Session & timing
        site_session_id: analyticsCtx.sessionId
          || document.querySelector('.page')?.dataset?.sessionId
          || null,
        timestamp_unix: Math.floor(Date.now() / 1000)
      });
    } catch (gaError) {
      console.error('GA tracking error:', gaError);
    }

    // 1. Record the FIRST position BEFORE any class changes
    const first = element.getBoundingClientRect();

    // 2. Show ghost element to hold space (CSS handles sizing dynamically - no inline styles needed)
    ghost.classList.remove('d-none');

    // 3. Set CSS custom properties for origin position (used by is-fs-transitioning)
    element.style.setProperty('--ce-origin-top', `${first.top}px`);
    element.style.setProperty('--ce-origin-left', `${first.left}px`);
    element.style.setProperty('--ce-origin-width', `${first.width}px`);
    element.style.setProperty('--ce-origin-height', `${first.height}px`);

    // 4. Add transitioning class to lock element at origin position (fixed, no transition)
    element.classList.add('is-fs-transitioning');

    // 5. Set body state
    // document.body.classList.add('modal-open');
    browserHelper.hideScrollbar();
    state.isFullscreen = true;

    // 6. Trigger reflow, then animate to fullscreen state
    requestAnimationFrame(() => {
      // Force reflow - reading offsetHeight triggers layout recalculation
      const forceReflow = element.offsetHeight; // eslint-disable-line no-unused-vars

      requestAnimationFrame(() => {
        // Remove transitioning (no transition), add animating (enables transition) + fullscreen (target state)
        element.classList.remove('is-fs-transitioning');
        element.classList.add('is-fs-animating');
        element.classList.add('fullscreen');

        // Portrait fullscreen rotates the container 90deg via CSS, which misaligns
        // touch/pointer coordinates for both Swiper and Pannellum.
        if (window.matchMedia('(orientation: portrait)').matches) {
          this.enableRotatedHandlers(state);
        }
      });
    });

    // Add escape key handler
    state.handlers.escapeHandler = (e) => {
      if (e.key === 'Escape' && state.isFullscreen) {
        const fullscreenCheckbox = elements.form.querySelector('input[name="mode"][value="fullscreen"]');

        if (fullscreenCheckbox) {
          fullscreenCheckbox.checked = false;
          fullscreenCheckbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    };
    document.addEventListener('keydown', state.handlers.escapeHandler);
  },

  /**
   * Enable rotated touch/drag mode on all Swipers and Pannellum viewers inside the element.
   * Delegates to swiperDirective.enableRotatedTouch and pannellumDirective.enableRotatedDrag.
   *
   * @param {State} state Element state
   */
  enableRotatedHandlers(state) {
    const { element } = state;

    element.querySelectorAll('.swiper').forEach((swiperEl) => {
      swiperEl.swiper?.enableRotatedTouch?.();
    });

    element.querySelectorAll('[amb-pannellum]').forEach((pnlmEl) => {
      if (pnlmEl._pannellumViewer) pannellumDirective.enableRotatedDrag(pnlmEl);
    });
  },

  /**
   * Disable rotated touch/drag mode on all Swipers and Pannellum viewers inside the element.
   *
   * @param {State} state Element state
   */
  disableRotatedHandlers(state) {
    const { element } = state;

    element.querySelectorAll('.swiper').forEach((swiperEl) => {
      swiperEl.swiper?.disableRotatedTouch?.();
    });

    element.querySelectorAll('[amb-pannellum]').forEach((pnlmEl) => {
      pannellumDirective.disableRotatedDrag(pnlmEl);
    });
  },

  /**
   * Close fullscreen mode using FLIP animation
   *
   * @param {object} state Element state
   */
  closeFullscreen(state) {
    const { element, elements } = state;
    const { ghost } = elements;

    if (!state.isFullscreen || !ghost) return;

    // 1. Get current ghost position (in case of scroll/resize)
    const ghostRect = ghost.getBoundingClientRect();

    // 2. Update origin CSS properties to current ghost position
    element.style.setProperty('--ce-origin-top', `${ghostRect.top}px`);
    element.style.setProperty('--ce-origin-left', `${ghostRect.left}px`);
    element.style.setProperty('--ce-origin-width', `${ghostRect.width}px`);
    element.style.setProperty('--ce-origin-height', `${ghostRect.height}px`);

    // 3. Remove rotated touch/pointer handlers
    this.disableRotatedHandlers(state);

    // 4. Switch from fullscreen to closing state (keep is-fs-animating for transition)
    element.classList.remove('fullscreen');
    element.classList.add('is-fs-closing');

    // 5. Cleanup after transition
    const cleanup = () => {
      if (!state.isFullscreen) return;

      // Remove all fullscreen classes
      element.classList.remove('is-fs-closing', 'is-fs-animating', 'is-fs-transitioning', 'fullscreen');

      // Remove CSS custom properties
      element.style.removeProperty('--ce-origin-top');
      element.style.removeProperty('--ce-origin-left');
      element.style.removeProperty('--ce-origin-width');
      element.style.removeProperty('--ce-origin-height');

      // Hide ghost
      ghost.classList.add('d-none');

      browserHelper.showScrollbar();
      // document.body.classList.remove('modal-open');

      // Remove escape handler
      if (state.handlers.escapeHandler) {
        document.removeEventListener('keydown', state.handlers.escapeHandler);
        state.handlers.escapeHandler = null;
      }

      state.isFullscreen = false;
    };

    // Listen for transitionend on the element itself (not children)
    const onTransitionEnd = (event) => {
      // Ignore events bubbling from child elements
      if (event.target !== element) {
        return;
      }

      // Only trigger on main transition properties
      if (!['top', 'left', 'width', 'height'].includes(event.propertyName)) {
        return;
      }

      // Remove listener and cleanup
      element.removeEventListener('transitionend', onTransitionEnd);
      cleanup();
    };

    element.addEventListener('transitionend', onTransitionEnd);
  },

  /**
   * Walk each configured exterior-spin and interior-panorama time.
   *
   * @private
   *
   * @param {Object} options Resolved explorer options
   * @param {function({ view: string, mode: string }, string): void} visit
   */
  eachConfigured360(options, visit) {
    for (const slot of this.VIEW_360_SLOTS) {
      const times = options.views?.[slot.view]?.[slot.mode] || [];

      for (const timeOfDay of times) {
        visit(slot, timeOfDay);
      }
    }
  },

  /**
   * Drop spin and panorama for every view.
   * SFCC static availability is left as already set.
   *
   * @private
   *
   * @param {State} state Element state
   */
  markSirvUnavailable(state) {
    const slots = this.VIEW_360_SLOTS.concat([{ view: 'boot', mode: 'spin' }]);

    for (const slot of slots) {
      const modeAvailability = state.availability?.[slot.view]?.[slot.mode];

      if (!modeAvailability) {
        continue;
      }

      modeAvailability.day = false;
      modeAvailability.night = false;
    }
  },

  /**
   * Clear configured 360 panes so the previous variant's viewer cannot linger.
   *
   * @private
   *
   * @param {State} state Element state
   */
  clearConfigured360Panes(state) {
    this.eachConfigured360(state.options, (slot, timeOfDay) => {
      this.clear360Pane(state, slot.view, timeOfDay);
    });
  },

  /**
   * @private
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @param {string} timeOfDay Time of day
   */
  clear360Pane(state, view, timeOfDay) {
    const tabPane = this.getTabPane(state, view, '360', timeOfDay);

    if (!tabPane) {
      return;
    }

    tabPane.innerHTML = '';
    tabPane.style.backgroundImage = '';
  },

  /**
   * Bind the exterior spin viewer for one time of day.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} productPath Variant Sirv path
   * @param {string} timeOfDay Time of day
   */
  bindExteriorSpin(state, productPath, timeOfDay) {
    const { baseUrl } = state;
    const url = `${baseUrl}/${productPath}/${timeOfDay}/360/360.spin`;
    const tabPane = this.getTabPane(state, 'exterior', '360', timeOfDay);

    if (tabPane) {
      tabPane.innerHTML = `<div class="Sirv" data-src="${url}"></div>`;
    }

    this.fetchTabPaneBackground(state, 'exterior', '360', timeOfDay, `${baseUrl}/${productPath}/${timeOfDay}/360-background.jpg`);
  },

  /**
   * Bind the interior panorama viewer for one time of day.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} productPath Variant Sirv path
   * @param {string} timeOfDay Time of day
   * @param {boolean} hasHotspots Whether panorama.json exists
   */
  bindInteriorPanorama(state, productPath, timeOfDay, hasHotspots) {
    const { baseUrl } = state;
    const panoramaUrl = `${baseUrl}/${productPath}/${timeOfDay}/panorama.jpg`;
    const hotspotsUrl = `${baseUrl}/${productPath}/${timeOfDay}/panorama.json`;
    const tabPane = this.getTabPane(state, 'interior', '360', timeOfDay);

    if (tabPane) {
      if (hasHotspots) {
        tabPane.innerHTML = `<div amb-pannellum="car_explorer, car_interior, ${timeOfDay}_mode" data-panorama="${panoramaUrl}" data-hot-spots="${hotspotsUrl}"></div>`;
      } else {
        tabPane.innerHTML = `<div amb-pannellum="car_explorer" data-panorama="${panoramaUrl}"></div>`;
      }
    }

    this.fetchTabPaneBackground(state, 'interior', '360', timeOfDay, `${baseUrl}/${productPath}/${timeOfDay}/panorama-background.jpg`);
  },

  /**
   * Load Sirv static images when this time of day is part of the static gallery.
   * No-op when static is not configured for that time.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @param {string} productPath Variant Sirv path
   * @param {string} timeOfDay Time of day
   *
   * @returns {Promise<boolean>|undefined}
   */
  fetchConfiguredStatic(state, view, productPath, timeOfDay) {
    const staticTimes = state.options.views?.[view]?.static || [];

    if (!staticTimes.includes(timeOfDay)) {
      return;
    }

    return this.fetchSirvStaticImages(state, view, productPath, timeOfDay);
  },

  /**
   * Reload every configured Sirv static time after the SFCC swiper write.
   * A later Sirv result can replace those slides. An empty Sirv folder does not.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} productPath Variant Sirv path
   *
   * @returns {Promise<void>}
   */
  loadConfiguredStaticGallery(state, productPath) {
    const { options } = state;
    const fetches = [];

    for (const [view, modes] of Object.entries(options.views || {})) {
      for (const timeOfDay of modes.static || []) {
        fetches.push(this.fetchSirvStaticImages(state, view, productPath, timeOfDay));
      }
    }

    for (const view of ['exterior', 'interior']) {
      const staticTimes = options.views?.[view]?.static || [];

      if (staticTimes.length > 0) {
        continue;
      }

      const mode = view === 'interior' ? 'panorama' : 'spin';

      for (const timeOfDay of ['day', 'night']) {
        const hadSirvStatic = this.sirvStaticCache.get(`${productPath}/${view}/${timeOfDay}`) === true;
        const spinMissing = state.availability?.[view]?.[mode]?.[timeOfDay] === false;

        if (hadSirvStatic && spinMissing) {
          fetches.push(this.fetchSirvStaticImages(state, view, productPath, timeOfDay));
        }
      }
    }

    return Promise.allSettled(fetches);
  },

  /**
   * Ignore per-image control updates until this gallery settles.
   *
   * @private
   *
   * @param {string} productPath Variant Sirv path
   *
   * @returns {number}
   */
  beginStaticGallery(productPath) {
    const token = this.staticGalleryToken + 1;

    this.staticGalleryToken = token;
    this.openStaticGallery.set(productPath, token);

    return token;
  },

  /**
   * @private
   *
   * @param {string} productPath Variant Sirv path
   * @param {number} token Gallery token from beginStaticGallery
   *
   * @returns {boolean} True when this gallery is still the latest one
   */
  endStaticGallery(productPath, token) {
    if (this.openStaticGallery.get(productPath) !== token) {
      return false;
    }

    this.openStaticGallery.delete(productPath);

    return true;
  },

  /**
   * @private
   *
   * @param {string} productPath Variant Sirv path
   *
   * @returns {boolean}
   */
  isStaticGalleryOpen(productPath) {
    return this.openStaticGallery.has(productPath);
  },

  /**
   * Drop static slides that this variant change will not replace with SFCC day images.
   * Night is never an SFCC set. An empty static list is the showroom 404 fallback pane.
   * Clearing bumps the swiper write id so an in-flight write from the previous variant is dropped.
   *
   * @private
   *
   * @param {State} state Element state
   */
  clearNightStaticPanes(state) {
    const { options } = state;

    for (const view of ['exterior', 'interior', 'boot']) {
      const staticTimes = options.views?.[view]?.static || [];

      this.clearStaticPane(state, view, 'night');

      if (staticTimes.length === 0) {
        this.clearStaticPane(state, view, 'day');
      }
    }
  },

  /**
   * @private
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @param {string} timeOfDay Time of day
   */
  clearStaticPane(state, view, timeOfDay) {
    const tabPane = this.getTabPane(state, view, 'static', timeOfDay);
    const swiper = tabPane?.querySelector('.swiper')?.swiper;

    if (swiper) {
      this.updateSwiperImages(swiper, []);
    }
  },

  /**
   * @private
   *
   * @param {string} productPath Variant the check belongs to
   * @param {string} view View name
   * @param {string} timeOfDay Time of day
   * @param {boolean} found Whether Sirv had a static image
   */
  rememberSirvStatic(productPath, view, timeOfDay, found) {
    this.sirvStaticCache.set(`${productPath}/${view}/${timeOfDay}`, found === true);
  },

  /**
   * Write static availability onto the variant that was checked.
   * A late response must not change the variant now on screen.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} productPath Variant the check belongs to
   * @param {string} view View name
   * @param {string} timeOfDay Time of day
   * @param {boolean} isAvailable Check result
   */
  recordStaticAvailability(state, productPath, view, timeOfDay, isAvailable) {
    if (state.variantKey === productPath) {
      this.setAvailability(state, view, 'static', timeOfDay, isAvailable);

      if (!this.isStaticGalleryOpen(productPath)) {
        this.updateControlsVisibility(state);
      }

      return;
    }

    const cached = this.availabilityCache.get(productPath);

    if (cached?.[view]?.static) {
      cached[view].static[timeOfDay] = isAvailable;
    }
  },

  /**
   * Load Sirv static after a missing 360 file.
   * A configured static list only allows its own times. An empty list keeps the old fallback.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} view View name
   * @param {string} productPath Variant Sirv path
   * @param {string} timeOfDay Time of day
   *
   * @returns {Promise<boolean>|false}
   */
  fetchStaticOnMiss(state, view, productPath, timeOfDay) {
    const staticTimes = state.options.views?.[view]?.static || [];

    if (staticTimes.length > 0 && !staticTimes.includes(timeOfDay)) {
      return false;
    }

    return this.fetchSirvStaticImages(state, view, productPath, timeOfDay);
  },

  /**
   * Point 360 panes at this variant, including one that was seen before.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} productPath Variant Sirv path
   */
  rebind360Viewers(state, productPath) {
    const { availability } = state;

    this.eachConfigured360(state.options, (slot, timeOfDay) => {
      const { view, mode } = slot;

      if (availability?.[view]?.[mode]?.[timeOfDay] === true) {
        if (mode === 'panorama') {
          const hasHotspots = this.panoramaHotspotsCache.get(`${productPath}/${timeOfDay}`) === true;

          this.bindInteriorPanorama(state, productPath, timeOfDay, hasHotspots);
        } else {
          this.bindExteriorSpin(state, productPath, timeOfDay);
        }
      } else {
        this.clear360Pane(state, view, timeOfDay);
      }
    });
  },

  /**
   * A cached variant is ready to rebind only after every configured spin and panorama
   * check has resolved. Pending `null` means the check is still in flight.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {Object} availability Cached availability
   *
   * @returns {boolean}
   */
  isSirvCheckSettled(state, availability) {
    let settled = true;

    this.eachConfigured360(state.options, (slot, timeOfDay) => {
      const { view, mode } = slot;
      const value = availability?.[view]?.[mode]?.[timeOfDay];

      if (value !== true && value !== false) {
        settled = false;
      }
    });

    return settled;
  },

  /**
   * Write a Sirv availability result onto the variant it was checked for.
   * A late response must not change the variant now on screen.
   *
   * @private
   *
   * @param {State} state Element state
   * @param {string} productPath Variant the check belongs to
   * @param {string} view View name
   * @param {string} mode Availability mode
   * @param {string} timeOfDay Time of day
   * @param {boolean} isAvailable Check result
   *
   * @returns {boolean} True when that variant is still showing
   */
  applyAvailabilityResult(state, productPath, view, mode, timeOfDay, isAvailable) {
    if (state.variantKey !== productPath) {
      const cached = this.availabilityCache.get(productPath);

      if (cached?.[view]?.[mode]) {
        cached[view][mode][timeOfDay] = isAvailable;
      }

      return false;
    }

    this.setAvailability(state, view, mode, timeOfDay, isAvailable);

    return true;
  },

  /**
   * Resolve the pending default-360 request for the current view.
   * Waits while that view's 360 check for the current time of day is in flight,
   * then settles once: 360 when it can show, otherwise the current mode stays.
   *
   * @private
   *
   * @param {State} state Element state
   *
   * @returns {string|undefined} '360' when the explorer should switch to it
   */
  takePreferredMode(state) {
    if (!state.prefer360Pending) {
      return;
    }

    const { element, availability } = state;
    const view = element.getAttribute('data-view') || 'exterior';
    const timeOfDay = element.getAttribute('data-time-of-day') || 'day';
    const slot = this.VIEW_360_SLOTS.find((item) => item.view === view);
    const checked = slot ? availability?.[view]?.[slot.mode]?.[timeOfDay] : false;

    if (checked !== true && checked !== false) {
      return;
    }

    state.prefer360Pending = false;

    if (this.getBestModeForView(state, view, '360', timeOfDay).mode !== '360') {
      return;
    }

    return '360';
  },
};
